import React, { useState } from 'react';
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Card from '../UI/Card';
import { RevenuePoint } from '../../api/services/dashboard.service';

interface RevenueTrendProps {
  monthlyData: RevenuePoint[];
}

const RevenueTrend: React.FC<RevenueTrendProps> = ({ monthlyData }) => {
  const [period, setPeriod] = useState<'week' | 'month'>('month');

  const weekData = monthlyData.slice(-7);
  const currentData = period === 'week' ? weekData : monthlyData;

  const chartData = currentData.map((item) => ({
    ...item,
    formattedRevenue: `ETB ${item.revenue.toLocaleString()}`,
  }));

  const totalRevenue = currentData.reduce((sum, d) => sum + d.revenue, 0);
  const averageRevenue = currentData.length
    ? Math.round(totalRevenue / currentData.length)
    : 0;
  const maxRevenue = currentData.length
    ? Math.max(...currentData.map((d) => d.revenue))
    : 0;
  const minRevenue = currentData.length
    ? Math.min(...currentData.map((d) => d.revenue))
    : 0;
  const trend =
    currentData.length > 1
      ? (((currentData[currentData.length - 1]?.revenue || 0) -
          (currentData[0]?.revenue || 0)) /
          (currentData[0]?.revenue || 1)) *
        100
      : 0;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-600">
            {payload[0].payload.day}
          </p>
          <p className="text-lg font-bold text-primary-600">
            ETB {payload[0].value.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {payload[0].payload.fullDate}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    const isMax = payload.revenue === maxRevenue;
    const isMin = payload.revenue === minRevenue && currentData.length > 1;

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={isMax ? 6 : isMin ? 6 : 4}
          fill={isMax ? '#10B981' : isMin ? '#EF4444' : '#d4a017'}
          stroke="white"
          strokeWidth={2}
        />
        {isMax && (
          <text x={cx} y={cy - 12} textAnchor="middle" fontSize="8" fill="#10B981" fontWeight="bold">
            MAX
          </text>
        )}
        {isMin && currentData.length > 2 && (
          <text x={cx} y={cy - 12} textAnchor="middle" fontSize="8" fill="#EF4444" fontWeight="bold">
            MIN
          </text>
        )}
      </g>
    );
  };

  return (
    <Card>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Revenue Trend</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {period === 'week' ? 'Last 7 days' : 'Last 30 days'} performance
          </p>
        </div>
        <div className="flex items-center gap-3 mt-3 sm:mt-0">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setPeriod('week')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                period === 'week'
                  ? 'bg-white text-gray-900 shadow-md'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setPeriod('month')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                period === 'month'
                  ? 'bg-white text-gray-900 shadow-md'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              30 Days
            </button>
          </div>
        </div>
      </div>

      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#d4a017" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#d4a017" stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} strokeOpacity={0.6} />

            <XAxis
              dataKey="day"
              stroke="#9CA3AF"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval={period === 'week' ? 0 : 4}
            />

            <YAxis
              stroke="#9CA3AF"
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => `ETB ${value.toLocaleString()}`}
              width={60}
            />

            <Tooltip content={<CustomTooltip />} />

            <Area type="monotone" dataKey="revenue" stroke="none" fill="url(#colorRevenue)" fillOpacity={1} />

            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#d4a017"
              strokeWidth={3}
              dot={<CustomDot />}
              activeDot={{ r: 8, stroke: '#d4a017', strokeWidth: 2, fill: 'white' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <p className="text-xs text-gray-500">Total Revenue</p>
            <p className="text-sm font-semibold text-gray-900">ETB {totalRevenue.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Daily Avg</p>
            <p className="text-sm font-semibold text-gray-900">ETB {averageRevenue.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Highest</p>
            <p className="text-sm font-semibold text-gray-900">ETB {maxRevenue.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Trend</p>
            <p className={`text-sm font-semibold ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default RevenueTrend;